import { action } from '@ember/object';
import { type Registry as Services, service } from '@ember/service';
import Component from '@glimmer/component';
import { PrismComboSelectInput } from '@mylighthouse/prism-ember';
import type { Option } from '@mylighthouse/prism-ember/types/components/prism/select-input';
import type { TaskForAsyncTaskFunction } from 'ember-concurrency';
import GeneralDropdownsTriggersTriggerPowerSelectKebab from 'lumen/components/general/dropdowns/triggers/trigger-power-select-kebab';
import type { BeniSmartCompset } from 'lumen/models';
import {
  BENI_SMART_COMPSET_ACTIONS,
  type BeniSmartCompsetAction,
} from 'lumen/utils/constants/benchmark-insight/beni-smart-compset-constant-values';

type TabSmartCompsetDetailsActionsOption = Option & {
  id: BeniSmartCompsetAction;
};

interface HotelBenchmarkInsightCompetitorsSmartCompsetTabSmartCompsetDetailsActionsSignature {
  Args: {
    saveTask: TaskForAsyncTaskFunction<
      unknown,
      (isSourceBanner: boolean) => Promise<void>
    >;
    smartCompset: BeniSmartCompset;
  };
}

export default class HotelBenchmarkInsightCompetitorsSmartCompsetTabSmartCompsetDetailsActions extends Component<HotelBenchmarkInsightCompetitorsSmartCompsetTabSmartCompsetDetailsActionsSignature> {
  @service declare alerting: Services['alerting'];
  @service declare intl: Services['intl'];
  @service declare usageAnalytics: Services['usage-analytics'];

  get smartCompsetActionsOptions(): TabSmartCompsetDetailsActionsOption[] {
    return [
      {
        id: BENI_SMART_COMPSET_ACTIONS.UPDATE,
        label: this.intl.t('benchmark-insight.smart-compset.update'),
        icon: 'sync',
        disabled: this.args.saveTask.isRunning,
      },
    ];
  }

  @action
  async onChange(option: TabSmartCompsetDetailsActionsOption): Promise<void> {
    if (option.id === BENI_SMART_COMPSET_ACTIONS.UPDATE) {
      const { saveTask, smartCompset } = this.args;

      this.usageAnalytics.track('Smart Compset Updating', {
        eventTrigger: 'smart-compset-settings',
      });

      const confirmWasClicked = await this.alerting.showConfirmWarningAlert({
        title: this.intl.t('benchmark-insight.smart-compset.update.title'),
        text: this.intl.t('benchmark-insight.smart-compset.update.body', {
          smartCompsetName: smartCompset.name,
        }),
        buttons: {
          cancel: {
            text: this.intl.t('general.cancel'),
          },
          confirm: {
            text: this.intl.t('benchmark-insight.smart-compset.update'),
          },
        },
      });

      if (!confirmWasClicked) {
        return;
      }

      await saveTask.perform(false);
    }
  }

  <template>
    <PrismComboSelectInput
      @horizontalPosition="right"
      @onChange={{this.onChange}}
      @options={{this.smartCompsetActionsOptions}}
      @unsafe_triggerComponent={{GeneralDropdownsTriggersTriggerPowerSelectKebab}}
    />
  </template>
}
